import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { RegionId } from '@snootsvet/shared'

import { PayButton, PaymentProcessing } from '@/customer/components/billing/billing-primitives'
import { PaymentMethods } from '@/customer/components/billing/payment-methods'
import { ContactSupportAction } from '@/customer/components/contact-support-action'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { useStripe } from '@/customer/hooks/use-stripe'

/**
 * Self-contained Stripe client-side payment widget. Handles card
 * selection, Stripe confirmation, error display, and the terminal
 * no-payment-path state. Calls `onSuccess` after a successful
 * confirmation; reconciliation and navigation are the caller's
 * responsibility.
 */
export function StripePaymentWidget({
  clientSecret,
  hostedInvoiceUrl,
  label,
  region,
  initialError,
  onSuccess,
}: {
  clientSecret: string | null
  hostedInvoiceUrl: string | null
  label: string
  region: RegionId
  /**
   * Pre-populated error message shown on first render. Used when
   * the widget is mounted after an initial action has already
   * failed (e.g. a server-side charge attempt or a prior Stripe
   * confirmation).
   */
  initialError?: string
  onSuccess: () => void
}) {
  const stripe = useStripe(region)
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null)
  const [stripeError, setStripeError] = useState<string | null>(initialError ?? null)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    if (initialError) setStripeError(initialError)
  }, [initialError])

  async function handleRetry() {
    if (!stripe || !clientSecret || !paymentMethodId) return
    setConfirming(true)
    setStripeError(null)

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId,
    })

    if (result.error) {
      setStripeError(result.error.message ?? 'Payment failed')
      setConfirming(false)
      return
    }

    setConfirming(false)
    onSuccess()
  }

  if (confirming) return <PaymentProcessing />

  if (!clientSecret && !hostedInvoiceUrl) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertTitle>We can&apos;t process this payment online</AlertTitle>
        <AlertDescription>
          Please get in touch with our team and we&apos;ll help resolve this.
        </AlertDescription>
        <AlertAction>
          <ContactSupportAction />
        </AlertAction>
      </Alert>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {stripeError && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Your payment didn't go through</AlertTitle>
          <AlertDescription>{stripeError}</AlertDescription>
        </Alert>
      )}

      {clientSecret && (
        <div className="flex flex-col gap-2">
          <h2 className="text-label-sm text-muted-foreground">Payment method</h2>
          <PaymentMethods value={paymentMethodId} onChange={setPaymentMethodId} />
        </div>
      )}

      <PayButton
        clientSecret={clientSecret}
        hostedInvoiceUrl={hostedInvoiceUrl}
        label={label}
        disabled={!paymentMethodId || !stripe}
        onClick={handleRetry}
      />
    </div>
  )
}
